import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function GenerateMissingDialogs() {
  const navigate = useNavigate();
  const [limit, setLimit] = useState("10");
  const [maxRetries, setMaxRetries] = useState("3");
  const [currentJob, setCurrentJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");
  const [missingCount, setMissingCount] = useState(null);

  // On mount, check if there is an existing non-completed job and show it
  useEffect(() => {
    const loadExistingJob = async () => {
      try {
        const { data: rows, error } = await supabase
          .from("dialog_generation_jobs")
          .select("*")
          .in("status", ["pending", "in_progress"]) // show one active job if exists
          .order("created_at", { ascending: false })
          .limit(1);

        if (error) {
          console.error("Error loading existing job:", error);
          setMessage(`Error loading existing job: ${error.message}`);
          return;
        }

        const jobRow = rows && rows.length > 0 ? rows[0] : null;

        if (jobRow) {
          setCurrentJob(jobRow.job_id);
          setJobStatus(jobRow);
          setMessage("Resumed existing background job.");
        } else {
          setCurrentJob(null);
          setJobStatus(null);
          setMessage("No active jobs found.");
        }
      } catch (err) {
        console.error("Error loading existing job:", err);
        setMessage(`Error loading existing job: ${err.message}`);
      }
    };

    loadExistingJob();
  }, []);

  // Fetch the total number of words that are missing dialogs (have fewer than 3)
  async function fetchMissingDialogsCount() {
    try {
      const { data, error } = await supabase.rpc("words_missing_dialogs_count");
      if (error) {
        console.error("Error fetching missing dialogs count:", error);
        return;
      }
      setMissingCount(typeof data === "number" ? data : null);
    } catch (err) {
      console.error("Error fetching missing dialogs count:", err);
    }
  }

  useEffect(() => {
    fetchMissingDialogsCount();
  }, []);

  const start = async () => {
    const num = parseInt(limit, 10);
    const retries = parseInt(maxRetries, 10);

    if (!num || num <= 0 || num > 1000) {
      alert("Please enter a valid number between 1 and 1000");
      return;
    }

    if (retries < 0 || retries > 10) {
      alert("Max retries must be between 0 and 10");
      return;
    }

    setRunning(true);
    setMessage("Creating job...");
    setCurrentJob(null);
    setJobStatus(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("No auth token available");

      // Create the job
      const { data: jobData, error: jobErr } = await supabase.functions.invoke(
        "create_dialog_generation_job",
        {
          body: {
            limit: num,
            batch_size: 1,
            max_retries: retries,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (jobErr) throw jobErr;

      if (!jobData.job_id) {
        setMessage(
          jobData.message || "No words found that need additional dialogs"
        );
        setRunning(false);
        return;
      }

      setCurrentJob(jobData.job_id);
      setJobStatus({
        job_id: jobData.job_id,
        status: jobData.status || "pending",
        processed_words: 0,
        failed_words: 0,
        total_words: jobData.total_words || 0,
        batch_size: jobData.batch_size,
        max_retries: jobData.max_retries,
      });
      setMessage(
        `Job created successfully! Processing ${jobData.total_words} words in background...`
      );

      // Job is now created and polling will start, so we're no longer in "creating" state
      setRunning(false);

      // Refresh the missing count (it may not change immediately, but this keeps the UI up to date)
      fetchMissingDialogsCount();
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Error creating job");
      setRunning(false);
    }
  };

  const stopJob = async () => {
    if (!currentJob) return;

    try {
      const { error } = await supabase
        .from("dialog_generation_jobs")
        .update({
          status: "cancelled",
          updated_at: new Date().toISOString(),
        })
        .eq("job_id", currentJob);

      if (error) throw error;

      // Clear the job state
      setCurrentJob(null);
      setJobStatus(null);
      setRunning(false);
      setMessage("Job cancelled successfully");
    } catch (err) {
      console.error("Error cancelling job:", err);
      setMessage("Error cancelling job: " + err.message);
    }
  };

  const formatJobStatus = (status) => {
    if (!status) return "";

    const {
      processed_words,
      total_words,
      failed_words,
      status: jobStatus,
    } = status;
    const progress =
      total_words > 0 ? Math.round((processed_words / total_words) * 100) : 0;

    return `Status: ${jobStatus} | Progress: ${processed_words}/${total_words} (${progress}%) | Failed: ${failed_words}`;
  };

  return (
    <div className="min-h-screen w-full bg-base-100 flex flex-col">
      <header className="bg-primary text-white py-6 shadow-md flex-shrink-0">
        <div className="container mx-auto px-4 flex items-center">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center text-white mr-6"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="text-3xl font-bold flex-1 text-center">
            Generate Missing Dialogs
          </h1>
          <div className="w-[76px]"></div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-4 py-8 max-w-xl">
          <div className="bg-white p-6 rounded-lg shadow-md space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Total words missing dialogs
              </div>
              <div className="flex items-center gap-3">
                <div className="font-mono text-sm">
                  {missingCount === null ? "…" : missingCount}
                </div>
                <button
                  className="btn btn-xs"
                  onClick={fetchMissingDialogsCount}
                  disabled={running}
                >
                  Refresh
                </button>
              </div>
            </div>

            {!currentJob && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Words to process
                  </label>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    min="1"
                    max="1000"
                    disabled={running}
                  />
                </div>

                {/* Batch size removed; always defaults to 1 */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max retries per word
                  </label>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    value={maxRetries}
                    onChange={(e) => setMaxRetries(e.target.value)}
                    min="0"
                    max="10"
                    disabled={running}
                  />
                </div>
              </>
            )}

            {currentJob && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Current Job</h3>
                <p className="text-sm text-blue-700 font-mono break-all mb-2">
                  ID: {currentJob}
                </p>
                {jobStatus && (
                  <p className="text-sm text-blue-700">
                    {formatJobStatus(jobStatus)}
                  </p>
                )}
                <p className="text-xs text-blue-600 mt-2">
                  💡 This job will continue running even if you close the
                  browser
                </p>
              </div>
            )}

            {message && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  message.includes("Error") || message.includes("Failed")
                    ? "bg-red-100 text-red-700"
                    : message.includes("cancelled")
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {message}
              </div>
            )}

            <div className="space-y-2">
              {!currentJob && (
                <button
                  onClick={start}
                  disabled={running}
                  className="btn btn-primary w-full"
                >
                  {running ? "Creating Job..." : "Start Background Job"}
                </button>
              )}

              {currentJob &&
                jobStatus &&
                jobStatus.status !== "completed" &&
                jobStatus.status !== "failed" &&
                jobStatus.status !== "cancelled" && (
                  <button onClick={stopJob} className="btn btn-error w-full">
                    Cancel Job
                  </button>
                )}
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">How it works</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Jobs run in the background using a queue system</li>
                <li>• You can close this page and jobs will continue</li>
                <li>• Jobs are processed automatically every 2 minutes</li>
                <li>• Failed words are retried up to the max retry limit</li>
                <li>• Each word gets 3 dialogs (text, image, and audio)</li>
              </ul>
            </div>
          </div>

          {/* Add extra spacing at bottom for better scrolling */}
          <div className="h-8"></div>
        </div>
      </main>
    </div>
  );
}
